import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Calendar, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Parent-dashboard "This Week's Schedule" — at-a-glance list of all
 * confirmed bookings between today and today+6 days. Includes athlete name,
 * date, and start time. Click row → go to schedule page.
 *
 * Pulls /api/schedule/my-bookings (returns booked sessions for the family),
 * filters in-memory to the next 7 days. Cheap, keeps the dashboard fast.
 */
export default function ThisWeekSchedule() {
    const [rows, setRows] = useState(null);
    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await axios.get(`${API}/schedule/my-bookings`, { withCredentials: true });
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const cutoff = new Date(today);
                cutoff.setDate(cutoff.getDate() + 7);
                const filtered = (r.data || [])
                    .filter((b) => {
                        if (!b.date) return false;
                        const d = new Date(b.date + "T00:00:00");
                        return d >= today && d < cutoff;
                    })
                    .sort((a, b) => {
                        const dateCmp = (a.date || "").localeCompare(b.date || "");
                        return dateCmp !== 0 ? dateCmp : (a.start_time || "").localeCompare(b.start_time || "");
                    });
                if (alive) setRows(filtered);
            } catch {
                if (alive) setRows([]);
            }
        })();
        return () => { alive = false; };
    }, []);

    if (rows === null) return null;

    return (
        <div className="border border-zinc-800 bg-zinc-950" data-testid="this-week-schedule">
            <div className="px-4 py-3 border-b border-zinc-900 bg-black flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-brand-red" />
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300">This Week</div>
                </div>
                <Link
                    to="/dashboard/schedule"
                    className="text-[10px] tracking-[0.18em] uppercase text-zinc-500 hover:text-white inline-flex items-center gap-1"
                    data-testid="this-week-schedule-all"
                >
                    Full schedule <ChevronRight className="w-3 h-3" />
                </Link>
            </div>
            {rows.length === 0 ? (
                <div className="px-4 py-6 text-center text-xs text-zinc-500">
                    No sessions booked this week. <Link to="/dashboard/schedule" className="text-brand-red hover:underline">Book one →</Link>
                </div>
            ) : (
                <div>
                    {rows.map((b) => <Row key={b.booking_id} b={b} />)}
                </div>
            )}
        </div>
    );
}

function Row({ b }) {
    const d = new Date(b.date + "T00:00:00");
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const diff = Math.round((d - today) / (1000 * 60 * 60 * 24));
    let dayLabel;
    if (diff === 0) dayLabel = "Today";
    else if (diff === 1) dayLabel = "Tomorrow";
    else dayLabel = d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" });
    return (
        <Link
            to="/dashboard/schedule"
            className="px-4 py-3 border-b border-zinc-900 last:border-b-0 flex items-center gap-3 hover:bg-zinc-900/40 transition-colors"
            data-testid={`this-week-row-${b.booking_id}`}
        >
            <div className="flex-1 min-w-0">
                <div className="text-sm text-white truncate">
                    {b.athlete_name || "Session"}
                    {b.label && <span className="text-zinc-500 text-xs ml-2">{b.label}</span>}
                </div>
                <div className="text-[11px] text-zinc-400 mt-0.5">{dayLabel} · {b.start_time}</div>
            </div>
            <ChevronRight className="w-4 h-4 text-zinc-600 shrink-0" />
        </Link>
    );
}
